const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/orderHotspotEventUtils.ts';
import { IAdministrationTime, ISchemaTime, ITimeRange } from '../defs/orderEventDefs';
import { Logger } from '../services/KisLoggerService';
import { compareStringWNumber, wildcardMatch } from './stringUtils';
import Imm from 'immutable';
import _ from 'lodash';
import moment from 'moment';
import { KISIM_TIME_FORMAT, KISIM_TIME_OPEN } from '../defs/generalDefs';
import { getMainConfig } from '../services/mainConfig';
import { ApprovalBy, EventData, IKatFkey } from '@cistec/common_web/defs/kisim';

const Log = Logger('orderHotspotEventUtils');

/**
 * Separates SENDER and ORDERNR/EVENTNR when building Ids
 */
export const senderNrSeparator = '_';

/**
 * Helper function to build orderId
 * @param order
 * @param channel
 * @returns {string}
 */
export function getOrderId(order: Imm.Map<string, any>, channel?: any) {
    if (!order) {
        return undefined;
    }
    const sender = order.get('SENDER');
    const nr = order.get('ORDERNR');
    if (!sender || !nr) {
        Log.warn(
            `getOrderId [${channel || ''}]: Received order with invalid values: SENDER = ${sender}; ORDERNR = ${nr};`
        );
        return undefined;
    }
    return `${sender}${senderNrSeparator}${nr}`;
}

/**
 * Helper function to build eventId
 * @param event
 * @param channel
 * @returns {string}
 */
export function getEventId(event: Imm.Map<string, any>, channel?: any) {
    const sender = event.get('SENDER');
    const nr = event.get('EVENTNR');
    if (!sender || !nr) {
        Log.warn(
            `getEventId [${channel || ''}]: Received order with invalid values: SENDER = ${sender}; EVENTNR = ${nr};`
        );
        return undefined;
    }
    return `${sender}${senderNrSeparator}${nr}`;
}

/**
 * Helper function to build refId
 * @param item order or event
 * @returns {string}
 */
export function getRefId(item: Imm.Map<string, any>) {
    const sender = item.get('REF_SENDER');
    const nr = item.get('REF_NR');
    return !sender || !nr ? '' : `${sender}${senderNrSeparator}${nr}`;
}

/**
 * Returns true if refId defined and not only the separator between SENDER and ORDERNR/EVENTNR, which might be the case
 * if there is no reference.
 * @param {string} refId
 * @return {boolean}
 */
export function isValidRefId(refId: string) {
    return refId && refId !== senderNrSeparator;
}

/**
 * Estimates by whom an order has to be approved.
 * @param order
 * @returns {ApprovalBy}
 */
export function getRequiredApproval(order: Imm.Map<string, any>): ApprovalBy {
    let approvalBy = ApprovalBy.unknown;
    if (order && order.has('VISSTAT')) {
        const requiresApprovalByDoctor = order.get('VISSTAT').includes('VSMORDAZT');
        const requiresApprovalByNurse = order.get('VISSTAT').includes('VSMORDPFL');
        const currentUserCanSetApproval = order.getIn(['RIGHTS', 'SetVisum']) === '1';

        if (!requiresApprovalByDoctor && !requiresApprovalByNurse) {
            approvalBy = ApprovalBy.none;
        } else if (requiresApprovalByDoctor && requiresApprovalByNurse) {
            approvalBy = currentUserCanSetApproval ? ApprovalBy.both : ApprovalBy.other;
        } else if (requiresApprovalByDoctor ^ requiresApprovalByNurse) {
            approvalBy = currentUserCanSetApproval ? ApprovalBy.currentUser : ApprovalBy.somebodyElse;
        }
    }

    return approvalBy;
}

export interface IIcon {
    name: string;
    color: iconColor;
}

export enum iconColor {
    black,
    red,
    blue,
}

const defecAmountConsistencyMap: Record<string, string> = {
    ';Kw': 'defec-wind',
    'Mk;': 'defec-none',
    'Mk;Kb': 'defec-none',
    'Mk;Kd': 'defec-none',
    'Mk;Kh': 'defec-none',
    'Mk;Kn': 'defec-none',
    'Mk;Kw': 'defec-wind',
    'Mn;Kb': 'defec-4b',
    'Mn;Kd': 'defec-4f',
    'Mn;Kh': 'defec-4h',
    'Mn;Kn': 'defec-4n',
    'Mn;Kw': 'defec-wind',
    'Ms;Kb': 'defec-8b',
    'Ms;Kd': 'defec-8f',
    'Ms;Kh': 'defec-8h',
    'Ms;Kn': 'defec-8n',
    'Ms;Kw': 'defec-wind',
    'Mv;Kb': 'defec-6b',
    'Mv;Kd': 'defec-6f',
    'Mv;Kh': 'defec-6h',
    'Mv;Kn': 'defec-6n',
    'Mv;Kw': 'defec-wind',
    'Mw;Kb': 'defec-2b',
    'Mw;Kd': 'defec-2f',
    'Mw;Kh': 'defec-2h',
    'Mw;Kn': 'defec-2n',
    'Mw;Kw': 'defec-wind',
};

export function defecationKindToIcon02(eventData: EventData): IIcon {
    const kind = eventData.KIND || '';
    const [amount, consistency, blood, newBorn] = kind.split(';');

    const name = (() => {
        if (amount !== 'Mk' && consistency !== 'Kw') {
            if (blood === 'Bm' || newBorn === 'Nm') return 'letter-m';
            if (blood === 'Bu' || newBorn === 'Nu') return 'letter-uuml';
        }
        return defecAmountConsistencyMap[`${amount};${consistency}`];
    })();

    const color = (() => {
        if (name === 'defec-none' || name === 'defec-wind') return iconColor.black;
        if (blood === 'Bk' || blood === 'Bm' || newBorn === 'Nm') return iconColor.black;
        if (blood === 'Bf') return iconColor.red;
        return iconColor.blue;
    })();

    return {
        name: !name ? '' : `icons8-${name}`,
        color,
    };
}

/**
 * Determine the to visualize the defecation based on the attribute `KIND` of the KISIM record
 * @returns An instance of IIcon
 */
export function defecationKindToIcon(eventData: EventData): IIcon {
    if (eventData.FCODE === 'MSNSTUHL02') return defecationKindToIcon02(eventData);

    // KIND is structured as follows: <amount>;<consistency>;<blood>
    // where
    //    amount: Mn=normal, Mw=wenig, Mv=viel, Ms=sehr viel, Mk=kein
    //    consistency: Kn=normal, Kh=hart, Kb=breiig, Kd=flüssig, Kw=Wind
    //    blood: ""=kein Blut, Bf=Frischblut, Bk=Melaena (does not exist on COM/dev: Bm, Nm, Bu, Nu)
    const kind = eventData.KIND || '';
    const [amount, consistency, blood] = kind.split(';');

    const name = (() => {
        if (amount !== 'Mk' && consistency !== 'Kw') {
            if (blood === 'Bm' || blood === 'Nm') return 'letter-m';
            if (blood === 'Bu' || blood === 'Nu') return 'letter-uuml';
        }
        return defecAmountConsistencyMap[`${amount};${consistency}`];
    })();

    const color = (() => {
        if (name === 'defec-none' || name === 'defec-wind') return iconColor.black;
        if (blood === 'Bk' || blood === 'Bm' || blood === 'Nm') return iconColor.black;
        if (blood === 'Bf') return iconColor.red;
        return iconColor.blue;
    })();

    return {
        name: !name ? '' : `icons8-${name}`,
        color,
    };
}

/**
 * Builds the summary from the given text and remarks. Intended to be used for orders and events.
 * Basically, an event's summary is given by the EVENTTXT and an order's summary by the ORDERTXT,
 * respectively. However, most events put the remarks somewhere into the EVENTTXT/ORDERTXT. This is
 * very clumsy, because it makes it impossible to render the two pieces of information separately.
 * This function is a hack that removes the remarks from the EVENTTXT/ORDERTXT.
 * @param text EVENTTXT or ORDERTXT
 * @param remarks Comment/remark of the event or order which should be dismissed from the text
 * @return: A string.
 */
export function buildEventOrderSummary(text: string, remarks: string) {
    if (!remarks) {
        // The remarks are undefined or empty.
        return text;
    }

    const where = text.indexOf(remarks);

    if (where < 0) {
        // The remarks is not part of the EVENTTXT.
        return text;
    }

    // Extract left and right parts.

    let lpart = text.slice(0, where).trim();
    let rpart = text.slice(where + remarks.length).trim();

    // Remove lpart's trailing delimiter.

    let removeIt = false;
    const lc = lpart.charAt(lpart.length - 1);

    if (lc === ';') {
        removeIt = true;
    } else if (lc === '/') {
        // Special case: Do not remove the trailing slash if the previous character indicates it
        // actually has a meaning. An example is the EVENTTXT of Pupillenskala when not all the
        // values have been specified.

        const lc2 = lpart.charAt(lpart.length - 2);

        if (lc2 !== '/' && lc2 !== '+' && lc2 !== ')' && lc2 !== '-') {
            removeIt = true;
        }
    }

    if (removeIt) {
        lpart = lpart.slice(0, lpart.length - 1).trim();
    }

    // Remove rpart's leading delimiter.

    const rc = rpart.charAt(0);

    if (rc === ';' || rc === '/') {
        rpart = rpart.slice(1).trim();
    }

    // Put the parts together.

    return !lpart ? rpart : !rpart ? lpart : `${lpart}; ${rpart}`;
}

/**
 * Determine the anchor date of an event. The events anchor date can be used to estimate if a hotspot has been
 * documented (by comparing the events anchor date with the hotspot date).
 * @param startdat      STARTDAT of event
 * @param stopdat       STOPDAT of event
 * @param kat           KAT of event
 * @return {string}     anchor date as string
 */
export function getEventsAnchorDate(startdat: string, stopdat: string, kat: string): string {
    if (stopdat && stopdat < KISIM_TIME_OPEN && kat !== 'MEDI') {
        // Using STOPDAT to match the event is kind of counter-intuitive, but in practice works better for Bilanz, Trinkmenge, etc. This
        // does not affect most events, because their STOPDAT equals STARTDAT anyway. With MEDI, startDat has to be used.
        return stopdat;
    } else {
        // This event is open-ended.
        return startdat;
    }
}

/**
 * Check if the combination of kat and fkey should be kept, which is the case if that combination is in
 * katsFkeysToKeep. If the FKEY is not defined for a certain KAT, all records of that KAT will be kept.
 * @param katsFkeysToKeep     Array with KATs and FKEYs to keep
 * @param kat           KAT of record
 * @param fkey          FKEY of record
 * @return {boolean}    true, if the record should be kept
 */
export function keepRecWithThisKatFkey(katsFkeysToKeep: IKatFkey[], kat: string, fkey: string) {
    if (_.isEmpty(katsFkeysToKeep)) {
        return false;
    }
    const katsFkeysThatWillBeKept = katsFkeysToKeep.filter((katFkey: IKatFkey) => {
        if (katFkey.KAT !== kat) {
            return false;
        }
        if (!katFkey.FKEY) {
            return true;
        }
        if (katFkey.FKEY === fkey) {
            return true;
        }
        if (wildcardMatch(katFkey.FKEY, fkey)) {
            return true;
        }
        return false;
    });
    return !_.isEmpty(katsFkeysThatWillBeKept);
}

/**
 * Build the name of a medication product
 * @param {Map<string, any>} item
 * @return {string}
 */
export function buildProductName(item: Imm.Map<string, any>): string {
    return (
        item.getIn(['CONTENT', 'MediField', 'Product']) ||
        (typeof item.getIn(['CONTENT', 'MediField']) === 'string' && item.getIn(['CONTENT', 'MediField'])) ||
        item.get('NAME') ||
        '-'
    );
}

/**
 * Get the correct name of a medication items application
 * @param {Map<string, any>} item
 * @return {string}
 */
export function buildApplication(item: Imm.Map<string, any>): string {
    let application: string;
    switch (item.get('KIND')) {
        case 'Infusion':
            application = 'als Infusion';
            break;
        case 'InInfusion':
            application = 'in Infusion';
            break;
        default:
            application = item.get('KIND');
    }
    return application;
}

/**
 * Checks if the schedule of the order is 'once'
 * @param {Map<string, any>} order
 * @return {boolean}
 */
export function isOrderSchemaOnce(order: Imm.Map<string, any>): boolean {
    return (
        order.getIn(['CONTENT', 'SchemaFrame']) === 'Once' || // medication order
        order.getIn(['CONTENT', 'Schedule', '1', 'DTType']) === 'Once'
    ); // other orders
}

/**
 * @return true if the given order is deleted; false otherwise
 */
export function isDeletedOrder(order: Imm.Map<string, any>) {
    return order.get('STAT1') === 'I';
}

/**
 * @return true if the given order is paused; false otherwise
 */
export function isPausedOrder(order: Imm.Map<string, any>) {
    return order.get('STAT1') === 'P';
}

/**
 * @return true if the given order is a Reserve-Medi; false otherwise
 */
export function isReserveOrder(order: Imm.Map<string, any>) {
    return order.get('PRIORITY') === 'R';
}

/**
 * @return true if the given order is an infusion solution; false otherwise
 */
export function isInfusionOrder(order: Imm.Map<string, any>) {
    return order.get('KAT') === 'MEDI' && order.get('KIND') === 'Infusion';
}

/**
 * Create a ghost/fake order for events which do not refer to an available order. There should be one (ghost) order for
 * each event. This ghost orders are necessary to sort orders and events.
 *
 * @param event     event for which one ghost order should be created
 * @return {Imm.Map<string, any>}   ghost order
 */
export function createGhostOrder(event: Imm.Map<string, any>): Imm.Map<string, any> {
    return Imm.fromJS({
        isGhostOrder: true,
        SENDER: `GHOST_${event.get('SENDER')}`,
        ORDERNR: event.get('EVENTNR'),
        NAME: event.get('NAME'),
        PATNR: event.get('PATNR'),
        FALLNR: event.get('FALLNR'),
        KIND: event.get('KIND'),
        STARTDAT: event.get('STARTDAT'),
        STOPDAT: event.get('STOPDAT'),
        KAT: event.get('KAT'),
        FKEY: event.get('FKEY'),
        FCODE: event.get('FCODE'),
        TYP: event.get('TYP'),
        STAT1: 'A',
        eventSender: event.get('SENDER'),
        eventNr: event.get('EVENTNR'),
        eventRefId: getRefId(event),
        unit: event.get('UNIT'),
        VISTYP: '',
        VISSTAT: '',
        REF_SENDER: '',
        REF_NR: '',
    });
}

/**
 * Add ghost orders to orders for all events which can not be displayed on top of an existing order. This is the case if
 * there is no reference to an available order.
 * @param orders    actually existing orders
 * @param events    all events (which might refer to an order or not)
 * @return          actually existing orders merged with ghost orders for events which do not refer to an existing order
 */
export function addMedicationGhostOrders(orders: Imm.Map<string, any>, events: Imm.Map<string, any>) {
    let ordersWGhosts = orders;
    events.forEach((event: Imm.Map<string, any>) => {
        if (!displayEventOnTopOfOrder(event, ordersWGhosts.get(getRefId(event)))) {
            let ghostOrder = createGhostOrder(event);
            let mediField = event.hasIn(['CONTENT', 'MediField']) && event.getIn(['CONTENT', 'MediField']);
            mediField = typeof mediField === 'object' ? mediField.toJS() : mediField;
            ghostOrder = ghostOrder.merge(
                Imm.fromJS({
                    CONTENT: {
                        PaidByPat: event.getIn(['CONTENT', 'PaidByPat']),
                        MediField: mediField,
                    },
                    COMM: '',
                })
            );
            const orderId = getOrderId(ghostOrder);
            if (orderId) {
                ordersWGhosts = ordersWGhosts.set(orderId, ghostOrder);
            }
        }
    });
    return ordersWGhosts;
}

/**
 * Returns true, if event should be displayed on top of order
 * @param {Map<string, any>} event
 * @param {Map<string, any>} order
 * @param {boolean} fkeyMustMatch
 * @return {boolean}
 */
export function displayEventOnTopOfOrder(event: Imm.Map<string, any>, order: Imm.Map<string, any>) {
    if (!event || !order) {
        return false;
    }
    let result = false;

    if (order.get('isGhostOrder')) {
        result = getGhostOrdersEventId(order) === getEventId(event);
    } else {
        // for some clients Bolus-Events are displayed in a separate row
        if (
            event.get('STATUS') === 'B' &&
            (getMainConfig().settings.client === 'SOHO' || getMainConfig().settings.client === 'SRO')
        ) {
            result = false;
        } else {
            result = getRefId(event) === getOrderId(order);
        }
    }

    return result;
}

/**
 * Get the EventId of the event which belongs to ghostOrder.
 * @param {Map<string, any>} ghostOrder
 * @return {string}
 */
function getGhostOrdersEventId(ghostOrder: Imm.Map<string, any>) {
    const sender = ghostOrder.get('eventSender');
    const nr = ghostOrder.get('eventNr');
    return !sender || !nr ? '' : `${sender}_${nr}`;
}

/**
 * Merged times of loaded hotspots, loaded events and configured times for schedule "schema"
 */
export const createAdministrationTimes = (
    hotspots: Imm.Set<string>,
    events: Imm.Set<string>,
    range: ITimeRange,
    schemaTimes: ISchemaTime[]
) => {
    const startTime = moment(range.startTime);
    const stopTime = moment(range.stopTime);
    let hotspotAndEventTimes = hotspots.merge(events);
    const administrationTimes: IAdministrationTime[] = [];

    const schemaHotspotTime = startTime.clone().startOf('day');
    let loopCount = 0;

    // Get times of schedule "schema" and add it to the hotspot times which will be displayed
    while (schemaHotspotTime.isSameOrBefore(stopTime) && loopCount < 25) {
        const schemaTimesIdx = loopCount % schemaTimes.length;
        schemaHotspotTime.hours(schemaTimes[schemaTimesIdx].hours).minutes(schemaTimes[schemaTimesIdx].minutes);

        if (schemaHotspotTime.isBetween(startTime, stopTime, null, '[]')) {
            const schemaHotspotTimeFormatted = schemaHotspotTime.format(KISIM_TIME_FORMAT);
            // hotspot is between start and stop time. Add it to displayed administration times
            administrationTimes.push({
                time: schemaHotspotTimeFormatted,
                schemaLabel: schemaTimes[schemaTimesIdx].label,
            });

            // remove hotspot and event times, which are the same as the schema times
            hotspotAndEventTimes = hotspotAndEventTimes.remove(schemaHotspotTimeFormatted);
        }

        // Jump to the next day, after the last administration time
        if ((loopCount + 1) % schemaTimes.length === 0) {
            schemaHotspotTime.add(1, 'day').startOf('day');
        }

        loopCount++;
    }

    // Add remaining hotspotAndEventTimes from loaded orders to administrationTimes
    hotspotAndEventTimes.forEach((hotspot: string) => {
        administrationTimes.push({
            time: hotspot,
        });
    });

    return administrationTimes.sort((administrationA: IAdministrationTime, administrationB: IAdministrationTime) =>
        compareStringWNumber(administrationA.time, administrationB.time)
    );
};
